import React, { useCallback, useEffect, useState } from "react";
import { AlertCircle, CheckCircle2, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import client from "../../lib/api";

const fmtMoney = (n, c = "EUR") =>
  new Intl.NumberFormat("pt-PT", { style: "currency", currency: c }).format(Number(n || 0));
const fmtDate = (s) =>
  s
    ? new Date(s).toLocaleDateString("pt-PT",
        { day: "2-digit", month: "short", year: "numeric" })
    : "—";
const pkgLabel = (id) => (
  { one_off_month: "1 mês", one_off_year: "1 ano",
    monthly: "Subscrição Mensal", annual: "Subscrição Anual" }[id] || id || "—"
);

/** Modal to confirm marking a tx as paid + capture bank reference for audit.
 *  Stripped-down: name, movement descriptor, admin note (optional). */
const MarkPaidModal = ({ tx, onClose, onDone }) => {
  const [bankRef, setBankRef] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!bankRef.trim()) {
      toast.error("Regista a referência do movimento bancário para auditoria.");
      return;
    }
    setBusy(true);
    try {
      const r = await client.post(
        `/admin/payments/${tx.session_id}/mark-paid`,
        { bank_reference: bankRef.trim(), note: note.trim() || null },
      );
      if (r.data?.already_paid) {
        toast.info("Transação já estava marcada como paga.");
      } else {
        toast.success("Premium ativado. Moloni será emitida em background.");
      }
      onDone?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao marcar como paga.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
         data-testid="mark-paid-modal" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md bg-[#161920] border border-white/10 rounded-2xl p-6">
        <h3 className="font-display text-xl text-[#F5F2EE] mb-1">
          Marcar pagamento como recebido
        </h3>
        <p className="text-sm text-[#A29F9B] mb-4">
          <b>{tx.user?.email || "utilizador desconhecido"}</b>
          {" · "}
          {pkgLabel(tx.package_id)}
          {" · "}
          {fmtMoney(tx.amount, (tx.currency || "eur").toUpperCase())}
          <br />
          <span className="text-xs">Ref. EuPago: {tx.eupago_entity || "?"} · {tx.eupago_reference || "?"}</span>
        </p>

        <label className="text-xs text-[#A29F9B] uppercase tracking-widest block mb-1">
          Referência do movimento bancário *
        </label>
        <input value={bankRef} onChange={(e) => setBankRef(e.target.value)}
               placeholder="Ex.: Pag Servicos *5104 21921-103602829"
               className="w-full bg-[#0C0E12] border border-white/10 rounded-lg px-3 py-2 text-sm text-[#F5F2EE] mb-3"
               data-testid="mark-paid-bank-ref" autoFocus />

        <label className="text-xs text-[#A29F9B] uppercase tracking-widest block mb-1">
          Nota interna (opcional)
        </label>
        <textarea value={note} onChange={(e) => setNote(e.target.value)}
                  rows={3}
                  placeholder="Ex.: cliente enviou comprovativo por email, webhook falhou."
                  className="w-full bg-[#0C0E12] border border-white/10 rounded-lg px-3 py-2 text-sm text-[#F5F2EE] mb-4"
                  data-testid="mark-paid-note" />

        <div className="flex items-center justify-end gap-2">
          <button type="button" onClick={onClose}
                  className="px-4 py-2 text-sm text-[#A29F9B] hover:text-white"
                  data-testid="mark-paid-cancel">
            Cancelar
          </button>
          <button type="submit" disabled={busy}
                  className="inline-flex items-center gap-2 bg-[#C5A880] text-[#0C0E12] font-semibold px-4 py-2 rounded-lg hover:bg-[#B89670] disabled:opacity-50"
                  data-testid="mark-paid-confirm">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            Marcar como pago
          </button>
        </div>
      </form>
    </div>
  );
};

/** Admin section: EuPago transactions that never reached "paid" — most of
 *  the time these are just abandoned carts, but sometimes the webhook simply
 *  never fired and the customer's bank statement shows the debit. Row-level
 *  action opens the modal to record the manual reconciliation. */
const PendingEuPagoSection = ({ onChanged }) => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await client.get("/admin/payments/eupago-pending?days=60");
      setRows(r.data?.rows || []);
      setTotal(r.data?.total || 0);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao carregar pendentes.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <section className="bg-[#161920] border border-white/5 rounded-2xl p-6"
             data-testid="pending-eupago-section">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div>
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-[#A29F9B] mb-1">
            <AlertCircle className="w-3.5 h-3.5 text-[#C5A880]" />
            EuPago pendentes (60 dias)
          </div>
          <h3 className="font-display text-2xl text-[#F5F2EE]">
            {total > 0 ? `${total} transaç${total === 1 ? "ão" : "ões"} por confirmar` : "Sem pendentes"}
          </h3>
          <p className="text-xs text-[#A29F9B] mt-1 max-w-xl">
            Se um cliente tiver comprovativo bancário mas o Premium não activou (webhook falhou),
            marca aqui como pago — extende o acesso e emite a factura Moloni em background.
          </p>
        </div>
        <button onClick={load} disabled={loading}
                className="inline-flex items-center gap-2 text-sm text-[#A29F9B] hover:text-white disabled:opacity-50"
                data-testid="pending-eupago-refresh">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Actualizar
        </button>
      </div>

      {rows.length === 0 && !loading && (
        <p className="text-sm text-[#A29F9B] italic">
          Nenhuma transação EuPago pendente nos últimos 60 dias.
        </p>
      )}

      {rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-widest text-[#A29F9B] border-b border-white/5">
                <th className="py-2 pr-4">Data</th>
                <th className="py-2 pr-4">Utilizador</th>
                <th className="py-2 pr-4">Pacote</th>
                <th className="py-2 pr-4">Valor</th>
                <th className="py-2 pr-4">Método</th>
                <th className="py-2 pr-4">Ref. EuPago</th>
                <th className="py-2 pr-4">Estado</th>
                <th className="py-2 pr-4"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => (
                <tr key={t.session_id} className="border-b border-white/5 text-[#DCD7D2]"
                    data-testid={`pending-eupago-row-${t.session_id}`}>
                  <td className="py-2 pr-4 whitespace-nowrap">{fmtDate(t.created_at)}</td>
                  <td className="py-2 pr-4">
                    {t.user_deleted ? (
                      <span className="text-[#A29F9B] italic">utilizador apagado</span>
                    ) : (
                      <>
                        <div>{t.user?.name || "—"}</div>
                        <div className="text-xs text-[#A29F9B]">{t.user?.email}</div>
                      </>
                    )}
                  </td>
                  <td className="py-2 pr-4">{pkgLabel(t.package_id)}</td>
                  <td className="py-2 pr-4 whitespace-nowrap">
                    {fmtMoney(t.amount, (t.currency || "eur").toUpperCase())}
                  </td>
                  <td className="py-2 pr-4 uppercase text-xs">{t.gateway_method || "—"}</td>
                  <td className="py-2 pr-4 font-mono text-xs">
                    {t.eupago_entity || "?"} · {t.eupago_reference || "?"}
                  </td>
                  <td className="py-2 pr-4">
                    <span className="text-xs text-[#C5A880]">
                      {t.payment_status || t.status || "created"}
                    </span>
                  </td>
                  <td className="py-2 pr-4 text-right">
                    <button onClick={() => setSelected(t)}
                            disabled={t.user_deleted}
                            className="text-xs bg-[#C5A880] text-[#0C0E12] font-semibold px-3 py-1.5 rounded-lg hover:bg-[#B89670] disabled:opacity-30 disabled:cursor-not-allowed"
                            data-testid={`mark-paid-open-${t.session_id}`}>
                      Marcar pago
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <MarkPaidModal
          tx={selected}
          onClose={() => setSelected(null)}
          onDone={() => { load(); onChanged?.(); }}
        />
      )}
    </section>
  );
};

export default PendingEuPagoSection;
